/* One managed gift per qualifying cart. Shopify, not this script, sets its price. */
(function (scope) {
  function createController(env) {
    const marker = '_emery_mold_gift';
    // Retain the historical marker to reconcile gifts already in customer carts.
    const markerValue = 'two-molds';
    const states = new Map();
    let queue = Promise.resolve();
    let pending = 0;
    let lastCart;
    let message = '';
    let blocked = false;
    let started = false;
    let checkoutPending = false;
    const resumedForms = new WeakSet();
    const resumedLinks = new WeakSet();
    const isManaged = (item) => item.properties?.[marker] === markerValue;
    const fingerprint = (cart) => JSON.stringify(cart?.items?.map((item) => [item.key, item.quantity, item.final_line_price]));
    const root = () => env.root?.() || '/';
    const config = () => env.getConfig?.() || {};
    const now = () => env.now?.() || Date.now();

    function stateFor(cart) {
      const key = `emery-mold-gift:${cart.token || 'current'}`;
      if (!states.has(key)) {
        try { states.set(key, JSON.parse(env.storage?.getItem(key) || '{}')); }
        catch { states.set(key, {}); }
      }
      return { key, value: states.get(key) };
    }

    function saveState(state) {
      try { env.storage?.setItem(state.key, JSON.stringify(state.value)); } catch { /* Storage is optional. */ }
    }

    function countMolds(cart, settings) {
      // Each eligible purchased item counts once, including the two-mold set.
      // Ignore legacy unit weights from the saved A/B control's configuration.
      const products = new Set((settings.products || []).map((product) => String(product.id)));
      return cart.items.reduce((total, item) => total + (!isManaged(item) && products.has(String(item.product_id)) ? item.quantity : 0), 0);
    }

    function hasExistingFreeContainer(cart, settings) {
      return cart.items.some((item) => {
        if (isManaged(item) || String(item.variant_id) !== String(settings.giftVariantId) || item.quantity < 1) return false;
        if (item.final_line_price === 0) return true;
        // A manually added quantity can include one free unit and additional paid
        // units. Only an explicit 100% allocation covering a whole unit qualifies;
        // an unrelated percentage-off sale is not a complimentary container.
        return Number(item.original_price) > 0 && item.line_level_discount_allocations?.some((allocation) =>
          allocation.amount >= item.original_price
          && allocation.discount_application?.value_type === 'percentage'
          && Number(allocation.discount_application.value) === 100
        );
      });
    }

    function renderMessage() {
      if (!env.document) return;
      for (const container of env.document.querySelectorAll('#cart-drawer [slot="footer"], .cart-order__recap')) {
        let banner = container.querySelector('[data-emery-gift-status]');
        if (!message) { banner?.remove(); continue; }
        if (!banner) {
          banner = env.document.createElement('p');
          banner.dataset.emeryGiftStatus = '';
          banner.className = 'text-sm';
          banner.style.cssText = 'padding:12px;border:1px solid currentColor;border-radius:8px;';
          banner.setAttribute('aria-live', 'polite');
          container.prepend(banner);
        }
        banner.setAttribute('role', blocked ? 'alert' : 'status');
        if (banner.textContent !== message) banner.textContent = message;
      }
    }

    function notify(text, state) {
      message = text;
      if (state) { state.value.warning = text; saveState(state); }
      renderMessage();
      env.onStatus?.(text, blocked);
    }

    function uncertain(cart) {
      blocked = true;
      const error = new Error('We could not verify the free container in your cart. Please refresh the page before checking out.');
      error.giftCleanupUncertain = true;
      error.cart = cart;
      notify(error.message);
      return error;
    }

    async function request(url, options = {}) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 12000);
      try {
        const response = await env.fetch(url, { ...options, signal: controller.signal });
        if (!response.ok) {
          const error = new Error(`Cart request failed (${response.status}).`);
          error.httpRejected = response.status >= 400 && response.status < 500 && response.status !== 408;
          throw error;
        }
        return await response.json();
      } finally { clearTimeout(timer); }
    }

    async function readCart() {
      const cart = await request(`${root()}cart.js`, { cache: 'no-store' });
      if (!Array.isArray(cart?.items)) throw new Error('The cart could not be read.');
      lastCart = cart;
      return cart;
    }

    async function writeAndRead(endpoint, body) {
      let failure;
      try {
        await request(`${root()}cart/${endpoint}.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
        });
      } catch (error) { failure = error; }
      const uncertainAdd = failure && !failure.httpRejected && endpoint === 'add';
      if (uncertainAdd) {
        const state = stateFor(lastCart || {});
        state.value.uncertainAdd = true;
        saveState(state);
      }
      // A timed-out write might have succeeded. Always inspect the actual cart before retrying.
      let cart;
      try { cart = await readCart(); } catch { throw uncertain(lastCart); }
      // An aborted POST can still commit after this GET. Do not interpret absence
      // as a rollback unless Shopify explicitly rejected the write.
      if (uncertainAdd) {
        const requestedGifts = body.items.filter((item) => item.properties?.[marker] === markerValue);
        const observed = requestedGifts.every((requested) => cart.items.some((item) => isManaged(item) && String(item.variant_id) === String(requested.id)));
        if (!observed) {
          const state = stateFor(cart);
          state.value.uncertainAdd = true;
          saveState(state);
          throw uncertain(cart);
        }
        const state = stateFor(cart);
        delete state.value.uncertainAdd;
        saveState(state);
      }
      return { cart, failure };
    }

    async function removeManaged(cart) {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const gifts = cart.items.filter(isManaged);
        if (!gifts.length) return cart;
        const updates = Object.fromEntries(gifts.map((item) => [item.key, 0]));
        ({ cart } = await writeAndRead('update', { updates }));
      }
      if (cart.items.some(isManaged)) throw uncertain(cart);
      return cart;
    }

    function collectSections() {
      const sections = [];
      if (env.document) {
        env.document.documentElement.dispatchEvent(new env.window.CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
        const main = env.document.querySelector('.shopify-section--main-cart');
        if (main?.id) sections.push(main.id.replace(/^shopify-section-/, ''));
      }
      return sections;
    }

    async function addSections(cart, options, original) {
      const sections = [...new Set([...(options.sections || []), ...collectSections()])].slice(0, 5);
      if (!sections.length) return cart;
      if (options.cart?.sections && fingerprint(options.cart) === fingerprint(cart) && fingerprint(original) === fingerprint(cart)) {
        cart.sections = options.cart.sections;
        return cart;
      }
      const origin = env.window?.location.origin || 'https://shop.test';
      const url = new URL(options.sectionsUrl || env.window?.location.href || root(), origin);
      url.searchParams.delete('section_id');
      url.searchParams.set('sections', sections.join(','));
      try { cart.sections = await request(`${url.pathname}${url.search}`, { cache: 'no-store' }); }
      catch { cart.sections = {}; } // Drawer hooks handle a failed section render with a fresh request.
      return cart;
    }

    async function sync(options = {}) {
      // Native add/change handlers already supply Shopify's current cart while
      // holding this controller's mutation lock. Reuse that response: a second
      // optional GET must not turn a successful customer action into an error.
      let cart = Array.isArray(options.cart?.items) ? options.cart : null;
      if (!cart) {
        try { cart = await readCart(); } catch { throw uncertain(lastCart); }
      }
      lastCart = cart;
      const original = cart;
      const settings = config();
      const state = stateFor(cart);
      if (state.value.uncertainAdd) {
        if (!options.recover) throw uncertain(cart);
        delete state.value.uncertainAdd;
        saveState(state);
      }
      const count = countMolds(cart, settings);
      if (count < 2) {
        state.value = {};
        states.set(state.key, state.value);
        saveState(state);
      }
      // When auto-add is disabled, maintain an existing gift safely but never
      // create a new gift or replace one the shopper has removed.
      const autoAdd = settings.autoAdd !== false;
      const eligible = settings.enabled && count >= 2 && settings.available && settings.giftVariantId
        && (autoAdd || cart.items.some(isManaged));
      const signature = JSON.stringify([settings.giftVariantId, (settings.products || []), cart.discount_codes || [], cart.items.filter((item) => !isManaged(item)).map((item) => [item.variant_id, item.quantity])]);
      if (!eligible || state.value.declined) {
        cart = await removeManaged(cart);
        blocked = false;
        notify(settings.enabled && count >= 2 && !settings.available && !state.value.declined
          ? 'The complimentary container is currently unavailable. You can still check out with your molds.' : '', state);
      } else {
        if (hasExistingFreeContainer(cart, settings)) cart = await removeManaged(cart);
        let gifts = cart.items.filter(isManaged);
        const keeper = gifts.find((item) => String(item.variant_id) === String(settings.giftVariantId) && item.final_line_price === 0)
          || gifts.find((item) => String(item.variant_id) === String(settings.giftVariantId));
        const updates = {};
        for (const gift of gifts) {
          const quantity = gift === keeper ? 1 : 0;
          if (gift.quantity !== quantity) updates[gift.key] = quantity;
        }
        if (Object.keys(updates).length) ({ cart } = await writeAndRead('update', { updates }));
        gifts = cart.items.filter(isManaged);
        const retrySuppressed = !options.force && state.value.failedSignature === signature && now() - state.value.failedAt < 60000;
        if (autoAdd && !gifts.length && !hasExistingFreeContainer(cart, settings) && !retrySuppressed) {
          ({ cart } = await writeAndRead('add', { items: [{ id: settings.giftVariantId, quantity: 1, properties: { [marker]: markerValue } }] }));
          gifts = cart.items.filter(isManaged);
        }
        // Shopify can choose the shopper's container for the discount after an
        // add. Preserve that line and remove only our now-unnecessary extra.
        if (hasExistingFreeContainer(cart, settings) && gifts.length) {
          cart = await removeManaged(cart);
          gifts = cart.items.filter(isManaged);
        }
        // Recheck both eligibility and price after Shopify has applied its discounts.
        const validManagedGift = gifts.length === 1 && gifts[0].quantity === 1
          && String(gifts[0].variant_id) === String(settings.giftVariantId) && gifts[0].final_line_price === 0;
        const valid = countMolds(cart, settings) >= 2 && (validManagedGift || hasExistingFreeContainer(cart, settings));
        if (valid) {
          blocked = false;
          delete state.value.failedSignature;
          delete state.value.failedAt;
          notify('', state);
        } else {
          cart = await removeManaged(cart);
          blocked = false;
          state.value.failedSignature = signature;
          state.value.failedAt = now();
          notify('The free container could not be applied. No paid container was added, and you can still check out.', state);
        }
      }
      lastCart = cart;
      cart.emeryGiftChanged = fingerprint(original) !== fingerprint(cart);
      return options.renderSections === false ? cart : addSections(cart, options, original);
    }

    function runMutation(operation) {
      pending += 1;
      const execute = () => operation(sync);
      const result = queue.then(() => env.locks?.request
        ? env.locks.request('emery-treat-mold-gift-cart', { mode: 'exclusive' }, execute) : execute());
      queue = result.catch(() => {});
      return result.finally(() => { pending -= 1; renderMessage(); });
    }

    async function noteRemoval(lineKey, quantity) {
      if (quantity !== 0) return;
      const settings = config();
      const knownLine = lastCart?.items.find((item) => item.key === lineKey);
      // Only a container removal can opt out of the gift. Ordinary lines from
      // the last verified cart need no extra request before their native write.
      if (knownLine && !isManaged(knownLine) && String(knownLine.variant_id) !== String(settings.giftVariantId)) return;
      const cart = await readCart();
      const removed = cart.items.find((item) => item.key === lineKey);
      const removesGift = removed && (isManaged(removed) || hasExistingFreeContainer({ items: [removed] }, settings));
      if (removesGift && countMolds(cart, settings) >= 2) {
        const state = stateFor(cart);
        state.value.declined = true;
        notify('', state);
      }
    }

    function publish(cart, replaceMain) {
      if (!env.document) return;
      if (replaceMain) {
        const main = env.document.querySelector('.shopify-section--main-cart');
        const html = main && cart.sections?.[main.id.replace(/^shopify-section-/, '')];
        if (html) {
          const parsed = new env.window.DOMParser().parseFromString(html, 'text/html');
          const replacement = parsed.getElementById(main.id);
          if (replacement) main.replaceChildren(...replacement.childNodes);
        }
      }
      env.document.documentElement.dispatchEvent(new env.window.CustomEvent('cart:change', {
        bubbles: true, detail: { baseEvent: 'gift:sync', cart, giftReconciled: true }
      }));
      renderMessage();
    }

    function reconcile(options = {}) {
      return runMutation(async (reconcileCart) => {
        const previous = lastCart;
        const cart = await reconcileCart(options);
        if (options.publish !== false) publish(cart, cart.emeryGiftChanged || (previous && fingerprint(previous) !== fingerprint(cart)));
        return cart;
      });
    }

    async function guardCheckout(event, form, link) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (checkoutPending) return;
      checkoutPending = true;
      const submitter = event.submitter;
      const fromDrawer = form?.closest('#cart-drawer');
      try {
        // Checkout needs the verified server cart, not replacement drawer HTML.
        // Publishing here tears down the active form (and app checkout hooks)
        // while its submit is paused, which can lose the navigation handoff.
        await reconcile({ force: true, publish: false, renderSections: false });
        if (blocked) return;
        if (link) { resumedLinks.add(link); link.click(); }
        else {
          // An independent app or pending UI update can still replace the form.
          // Recommendation Add forms also have actions, so target checkout explicitly.
          const target = form.isConnected ? form : env.document.querySelector(fromDrawer ? '#cart-drawer form[data-cart-checkout]' : 'form.cart-form');
          if (!target) return;
          const button = submitter?.isConnected && submitter.form === target ? submitter : target.querySelector('[name="checkout"]');
          if (!button || button.form !== target) return;
          resumedForms.add(target);
          target.requestSubmit(button);
        }
      } catch (error) { notify(error.message); }
      finally { checkoutPending = false; }
    }

    function start() {
      if (started || !env.document) return;
      started = true;
      const schedule = () => reconcile().catch((error) => notify(error.message));
      env.window.addEventListener('pageshow', (event) => { if (event.persisted) schedule(); });
      env.document.addEventListener('visibilitychange', () => { if (env.document.visibilityState === 'visible') schedule(); });
      env.document.addEventListener('cart:refresh', schedule);
      env.document.addEventListener('cart:change', (event) => { if (!event.detail?.giftReconciled) schedule(); });
      env.document.addEventListener('submit', (event) => {
        const form = event.target;
        if (resumedForms.delete(form)) return;
        if (event.submitter?.name === 'checkout') guardCheckout(event, form);
      }, true);
      env.document.addEventListener('click', (event) => {
        const link = event.target.closest?.('a[href]');
        if (!link || resumedLinks.delete(link)) return;
        const url = new URL(link.href, env.window.location.origin);
        if (url.origin === env.window.location.origin && /\/checkout\/?$/.test(url.pathname)) guardCheckout(event, null, link);
      }, true);
      new env.window.MutationObserver(renderMessage).observe(env.document.body, { childList: true, subtree: true });
      // A fresh page load is an explicit recovery checkpoint. Visibility/cart
      // events never clear an unresolved transport write automatically.
      reconcile({ recover: true }).catch((error) => notify(error.message));
    }

    return { runMutation, reconcile, noteRemoval, start, get pending() { return pending; }, get blocked() { return blocked; }, get message() { return message; } };
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { createController };
  else if (!scope.EmeryTreatMoldGift) {
    scope.EmeryTreatMoldGift = createController({
      window: scope, document: scope.document, fetch: scope.fetch.bind(scope), locks: scope.navigator.locks,
      get storage() { try { return scope.sessionStorage; } catch { return null; } },
      root: () => scope.Shopify?.routes.root || '/',
      getConfig: () => { try { return JSON.parse(scope.document.getElementById('treat-mold-gift-config')?.textContent || '{}'); } catch { return {}; } }
    });
    // A saved-control bridge can load this script synchronously in the header.
    // Claim the global now so the old deferred controller cannot start twice,
    // then wait until the cart and remaining document have been parsed.
    if (scope.document.readyState === 'loading') {
      scope.document.addEventListener('DOMContentLoaded', () => scope.EmeryTreatMoldGift.start(), { once: true });
    } else {
      scope.EmeryTreatMoldGift.start();
    }
  }
})(typeof window === 'undefined' ? globalThis : window);
